// Central per-page SEO config for the public marketing site + site-wide JSON-LD.
import { siteBase } from '../components/Seo';

export const SITE_NAME = 'BillPortal';
const K = (s) => s.split(',').map((x) => x.trim());

// Static routes → SEO. Dynamic detail routes (/blog/:slug, /solutions/:slug,
// /case-studies/:slug, /legal/:slug, /lp/:slug) set their own <Seo> in-page.
export const PAGE_SEO = {
  '/': {
    title: 'AI Utility Bill & Expense Management',
    description: 'BillPortal is the AI-powered platform to collect, validate and pay every utility bill on one system — audit-ready data for finance, operations and sustainability teams.',
    keywords: K('utility bill management, expense management, AI bill parsing, utility payments, bill validation, ENERGY STAR reporting'),
  },
  '/platform': {
    title: 'The Platform — Collect, Validate & Pay Utility Bills',
    description: 'One platform for the full utility bill lifecycle: AI collection agents, parsing across every commodity, exception-based validation and NACHA bulk ACH payments.',
    keywords: K('utility bill platform, AI collection agents, bill parsing, exception validation, ACH bulk payments, NACHA CTX CCD'),
  },
  '/platform/astrid': {
    title: 'Meet Astrid — Your AI Support Agent for Utility Operations',
    description: "Astrid is BillPortal's conversational utility-operations specialist. Ask about bills, vendors, payments, migrations and sustainability — grounded in permission-scoped BillPortal data, with review before action.",
    keywords: K('Astrid AI agent, BillPortal AI assistant, utility operations AI, AI bill assistant, AI support agent'),
  },
  '/platform/onboarding-planner': {
    title: 'Onboarding Planner — Go Live with Confidence',
    description: "BillPortal's Onboarding Planner gives every organisation a pre-built implementation and cutover checklist. Plan, assign and track every onboarding activity from setup to go-live in one collaborative workspace.",
    keywords: K('utility onboarding, implementation planner, cutover tracker, BillPortal onboarding, go-live checklist, UBM onboarding'),
  },
  '/pricing': {
    title: 'Pricing — Plans for Every Portfolio',
    description: 'Simple, transparent BillPortal pricing for multifamily, commercial & industrial and energy-broker teams. Scale from a single site to thousands of accounts.',
    keywords: K('billportal pricing, utility bill software pricing, expense management pricing'),
  },
  '/solutions': {
    title: 'Solutions by Industry',
    description: 'Purpose-built utility bill and expense management for multifamily operators, commercial & industrial real estate, energy brokers and bill-validation providers.',
    keywords: K('utility management solutions, multifamily utility management, commercial real estate utilities, energy broker software'),
  },
  '/case-studies': {
    title: 'Case Studies & Customer Outcomes',
    description: 'See how operators cut utility costs, recovered vacant-unit spend and built audit-ready data with BillPortal.',
    keywords: K('utility management case studies, cost recovery, NOI improvement, utility data'),
  },
  '/blog': {
    title: 'Blog — Utility & Expense Management Insights',
    description: 'Practical guidance on utility bill management, AI parsing accuracy, bulk payments, cost recovery and sustainability reporting from the BillPortal team.',
    keywords: K('utility bill management blog, expense management insights, AI bill parsing, sustainability reporting'),
  },
  '/how-it-works': {
    title: 'How It Works',
    description: 'From bill capture to validated, paid and analysed — see how BillPortal automates the utility bill lifecycle end to end.',
    keywords: K('how utility bill management works, bill automation workflow, AP automation utilities'),
  },
  '/about': {
    title: 'About BillPortal',
    description: 'BillPortal builds the AI-powered system of record for utility bills and expenses — helping finance, operations and sustainability teams take control of every bill.',
    keywords: K('about billportal, utility bill management company'),
  },
  '/demo': {
    title: 'Request a Demo',
    description: 'Book a personalised BillPortal demo and see how to centralise, validate and pay every utility bill with audit-ready data.',
    keywords: K('billportal demo, utility bill software demo'),
  },
  '/book-a-call': {
    title: 'Book a Call',
    description: 'Talk to the BillPortal team about automating your utility bill and expense management.',
    keywords: K('book a call, utility management consultation'),
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with the BillPortal team — sales, support and partnerships.',
    keywords: K('contact billportal, utility bill management contact'),
  },
  '/support': {
    title: 'Support',
    description: 'Get help with BillPortal. Submit a support, billing or general request and our team will respond.',
    keywords: K('billportal support, utility software help'),
  },
  '/partners': {
    title: 'Partner Programme',
    description: 'Join the BillPortal partner programme — bill-validation providers, energy brokers and consultants delivering utility management on a modern platform.',
    keywords: K('billportal partners, utility management partner programme, energy broker partnership'),
  },
  '/legal': {
    title: 'Legal',
    description: 'BillPortal legal centre — terms of service, privacy policy and partner terms.',
    keywords: K('billportal legal, terms of service, privacy policy'),
  },
};

export function getPageSeo(pathname) {
  return PAGE_SEO[pathname] || null;
}

// Site-wide structured data injected on every route.
export function siteJsonLd() {
  const base = siteBase();
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: base || 'https://billportal.io',
      logo: `${base}/BP-Mark.png`,
      description: 'AI-powered utility bill and expense management platform.',
      sameAs: ['https://www.linkedin.com/company/billportal'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: base || 'https://billportal.io',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${base}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ];
}

export function breadcrumbJsonLd(items) {
  const base = siteBase();
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${base}${it.path}`,
    })),
  };
}

export function articleJsonLd({ title, description, image, path, datePublished, dateModified }) {
  const base = siteBase();
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image ? (image.startsWith('http') ? image : `${base}${image}`) : `${base}/BP-Mark.png`,
    mainEntityOfPage: `${base}${path}`,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: `${base}/BP-Mark.png` } },
    datePublished: datePublished || undefined,
    dateModified: dateModified || datePublished || undefined,
  };
}

export function faqJsonLd(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q || f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.a || f.answer },
    })),
  };
}
